<template>
  <div :class="['input', type === 'password' ? 'input--password' : '']">
    <div
      v-if="noLabels === false"
      :class="['input__message', error && !hasFocus ? 'input__message--error' : 'input__message--label']"
    >
      <span v-if="hasFocus">{{ altLabel }}</span>
      <span v-else>{{ error ? error : label }}</span>

      <div
        v-if="type === 'password'"
        class="input__visibility"
        @click="togglePasswordVisibility()"
      >
        <svg v-if="toggledType === 'password'" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M12.015 7c4.751 0 8.063 3.012 9.504 4.636-1.401 1.837-4.713 5.364-9.504 5.364-4.42 0-7.93-3.536-9.478-5.407 1.493-1.647 4.817-4.593 9.478-4.593zm0-2c-7.569 0-12.015 6.551-12.015 6.551s4.835 7.449 12.015 7.449c7.733 0 11.985-7.449 11.985-7.449s-4.291-6.551-11.985-6.551zm-.015 3c-2.21 0-4 1.791-4 4s1.79 4 4 4c2.209 0 4-1.791 4-4s-1.791-4-4-4zm-.004 3.999c-.564.564-1.479.564-2.044 0s-.565-1.48 0-2.044c.564-.564 1.479-.564 2.044 0s.565 1.479 0 2.044z"/></svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 24 24"><path d="M19.604 2.562l-3.346 3.137c-1.27-.428-2.686-.699-4.243-.699-7.569 0-12.015 6.551-12.015 6.551s1.928 2.951 5.146 5.138l-2.911 2.909 1.414 1.414 17.37-17.035-1.415-1.415zm-6.016 5.779c-3.288-1.453-6.681 1.908-5.265 5.206l-1.726 1.707c-1.814-1.16-3.225-2.65-4.06-3.66 1.493-1.648 4.817-4.594 9.478-4.594.927 0 1.796.119 2.61.315l-1.037 1.026zm-2.883 7.431l5.09-4.993c1.017 3.111-2.003 6.067-5.09 4.993zm13.295-4.221s-4.252 7.449-11.985 7.449c-1.379 0-2.662-.291-3.851-.737l1.614-1.583c.715.193 1.458.32 2.237.32 4.791 0 8.104-3.527 9.504-5.364-.729-.822-1.956-1.99-3.587-2.952l1.489-1.46c2.982 1.9 4.579 4.327 4.579 4.327z"/></svg>
      </div>
    </div>

    <div
      :class="['input__field', 'input__field--' + variant, 'input__field--' + size]"
      @click="$refs[refName].focus()"
    >
      <div
        v-if="icon"
        :class="['input__icon', 'input__icon--' + iconSize, iconColor ? 'input__icon--' + iconColor : '', input.length > 0 || hasFocus ? 'input__icon--activated' : '', prefilled ? 'input__icon--prefilled' : '']"
        v-html="icon"
      />

      <input
        v-model="type === 'select' ? selectInput : input"
        :id="refName"
        :ref="refName"
        :class="['input__element', type === 'select' ? 'input__element--select' : '', 'input__element--' + variant, 'input__element--' + size, status === 'invalid' ? 'input__element--error' : '', status === 'valid' ? 'input__element--success' : '', hasFocus ? 'input__element--active' : '', selected ? 'input__element--selected' : '', prefilled ? 'input__element--prefilled' : '', rounded ? 'input__element--rounded' : '', squared ? 'input__element--squared' : '', disabled ? 'input__element--disabled' : '']"
        :type="toggledType"
        :placeholder="placeholder"
        :autofocus="autofocus"
        :autocomplete="autocomplete"
        :disabled="disabled"
        tabindex="0"
        @focus="hasFocus = true; $emit('focused', $event)"
        @blur="hasFocus = false; $emit('unfocused', $event)"
        @keyup.down="$emit('scrolledDown')"
        @keyup.up="$emit('scrolledUp')"
        @keydown="handleReadonly"
        @change="$emit('inputChanged', type === 'select' ? selectInput : input)"
      >
    </div>
  </div>
</template>

<script>
export default {
  props: {
    value: {
      type: [Object, Number, String, Boolean],
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    prefilled: {
      type: Boolean,
      default: false
    },
    refName: {
      type: String,
      default: 'input'
    },
    noLabels: {
      type: Boolean,
      default: false
    },
    label: {
      type: String,
      default: ''
    },
    altLabel: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String,
      default: ''
    },
    size: {
      type: String,
      default: 'md',
      validator (values) {
        return ['sm', 'md'].includes(values)
      }
    },
    iconSize: {
      type: String,
      default: 'md',
      validator (values) {
        return ['xs', 'sm', 'md', 'lg'].includes(values)
      }
    },
    iconColor: {
      type: [Boolean, String],
      default: false
    },
    variant: {
      type: String,
      default: 'dark',
      validator (values) {
        return ['dark', 'light', 'darklight'].includes(values)
      }
    },
    rounded: {
      type: Boolean,
      default: false
    },
    squared: {
      type: Boolean,
      default: false
    },
    icon: {
      type: [Boolean, String, Array],
      default: false
    },
    autocomplete: {
      type: String,
      default: 'off'
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    selected: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: [String, Boolean],
      default: false
    },
    status: {
      type: [String, Boolean],
      default: false
    }
  },
  data () {
    return {
      input: this.value,
      selectInputValue: this.value,
      toggledType: this.type,
      hasFocus: false
    }
  },
  computed: {
    selectInput: {
      get () {
        return this.value
      },
      set (newValue) {
        this.selectInputValue = newValue
       }
    }
  },
  methods: {
    togglePasswordVisibility () {
      this.toggledType = this.toggledType === 'password' ? 'text' : 'password'
    },
    handleReadonly (event) {
      if (this.readonly === true) {
        event.preventDefault()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/app.scss';

.input {
  @include font('content--md');
  position: relative;
  width: 100%;

  @media screen and (min-width: $breakpointLg) {
    @include font('content--lg');
  }

  &__visibility {
    position: absolute;
    top: 0;
    right: 0;
    text-align: right;
    user-select: none;
    width: 24px;
    height: 24px;
    fill: rgba($white, 0.5);
    transition: fill 0.2s ease-out;
    cursor: pointer;

    @media screen and (min-width: $breakpointLg) {
      width: 26px;
      height: 26px;
    }

    &:hover {
      fill: $white;
    }
  }

  &__message {
    position: relative;
    width: 100%;
    text-align: left;

    &--label {
      color: rgba($white, 0.9);
    }

    &--error {
      color: $danger;
    }
  }

  &__field {
    position: relative;
    /*position: relative;
    top: 0;
    left: 0;*/
    width: 100%;

    &:hover:is(.input__field--dark) {
      .input__icon {
        fill: $white;
      }

      .input__element:not(.input__element--active):not(.input__element--disabled) {
        background-color: darken($bg-1, 4);
      }

      .input__element:not(.input__element--active):not(.input__element--success):not(.input__element--error) {
        border-color: rgba($white, 0.4);
      }
    }

    &:hover:is(.input__field--light) {
      .input__icon {
        fill: $white;
      }

      .input__element:not(.input__element--active) {
        background-color: $bg-2;
      }

      .input__element:not(.input__element--active):not(.input__element--success):not(.input__element--error) {
        border-color: rgba($white, 0.4);
      }
    }

    &:hover:is(.input__field--darklight) {
      .input__icon {
        fill: $white;
      }

      .input__element:not(.input__element--active) {
        background-color: $bg-2;
      }

      .input__element:not(.input__element--active):not(.input__element--success):not(.input__element--error) {
        border-color: rgba($white, 0.4);
      }
    }

    &--sm {
      height: 36px;

      @media screen and (min-width: $breakpointLg) {
        height: 38px;
      }
    }

    &--md {
      height: 40px;

      @media screen and (min-width: $breakpointLg) {
        height: 42px;
      }
    }
  }

  &__icon {
    position: absolute;
    z-index: 6;
    top: 0;
    left: 0;
    fill: rgba($white, 0.5);
    transition: fill 0.2s ease-out;

    &--activated, &--prefilled {
      fill: $white;
    }

    &--xs {
      width: 16px;
      height: 16px;
      padding: 12px 10px;

      @media screen and (min-width: $breakpointLg) {
        width: 18px;
        height: 18px;
      }
    }

    &--sm {
      width: 18px;
      height: 18px;
      padding: 11px 10px;

      @media screen and (min-width: $breakpointLg) {
        width: 20px;
        height: 20px;
      }
    }

    &--md {
      width: 20px;
      height: 20px;
      padding: 10px 10px;

      @media screen and (min-width: $breakpointLg) {
        width: 22px;
        height: 22px;
      }
    }

    &--lg {
      width: 22px;
      height: 22px;
      padding: 9px 10px;

      @media screen and (min-width: $breakpointLg) {
        width: 24px;
        height: 24px;
      }
    }

    &--light {
      fill: rgba($white, 0.5);
    }
  }

  &__element {
    position: absolute;
    z-index: 5;
    top: 0;
    left: 0;
    width: calc(100% - 54px);
    padding: 0 14px 0 36px;
    outline: 0;
    border-style: solid;
    border-color: $bg-1;
    border-width: 0 0 0 4px;
    background-color: $bg-1;
    color: $white;
    transition: background-color 0.2s ease-out, border 0.2s ease-out;

    &::placeholder {
      color: rgba($white, 0.3);
    }

    &:focus, &:active, &--active {
      background-color: darken($bg-1, 4);
      color: $white;
      border-color: rgba($white, 0.9);
    }

    &:disabled {
      background-color: lighten($bg-2, 15);
      border-color: lighten($bg-2, 15);
      color: rgba($white, 0.5);
    }

    &:not(.input__element--squared) {
      border-radius: 4px;
    }

    &--select {
      caret-color: transparent;
      user-select: none;
    }

    &--selected, &--prefilled {
      &::placeholder {
        color: $white !important;
      }
    }

    &--sm {
      height: 36px;

      @media screen and (min-width: $breakpointLg) {
        height: 38px;
      }
    }

    &--md {
      height: 40px;

      @media screen and (min-width: $breakpointLg) {
        height: 42px;
      }
    }

    &--dark {
      background-color: $bg-1;
      border-color: $bg-1;
    }

    &--light {
      background-color: $bg-3;
      border-color: $bg-3;
    }

    &--darklight {
      background-color: $bg-2;
      border-color: $bg-2;
    }

    &--rounded {
      border-radius: 999px;
    }

    &--squared {
      border-radius: 0px;
      border-color: rgba($white, 0.1);
    }

    &--success {
      color: $white;
      border-color: $success;
    }

    &--error {
      color: $white;
      border-color: $danger;
    }
  }
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus {
  -webkit-text-fill-color: $white;
  -webkit-box-shadow: 0 0 0px 40rem $bg-1 inset;
}

</style>
